(() => {
  const namespace = 'ObriDispatchEstimate';

  if (window[namespace]) {
    window[namespace].renderAll();
    return;
  }

  const selector = '[data-obri-dispatch]';
  const valueSelector = '[data-obri-dispatch-value]';
  const dayMs = 24 * 60 * 60 * 1000;
  const mondayCutoffMinutes = 14 * 60;
  const fallbackTimeZone = 'Europe/Kiev';
  const formatterCache = new Map();
  let refreshTimer = null;

  const toInteger = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const dateKey = (date) => {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  };

  const addCalendarDays = (date, amount) => {
    const next = new Date(date.getTime());
    next.setUTCDate(next.getUTCDate() + amount);
    return next;
  };

  const getFormatter = (key, locale, options) => {
    if (!formatterCache.has(key)) {
      formatterCache.set(key, new Intl.DateTimeFormat(locale, options));
    }

    return formatterCache.get(key);
  };

  const zonedParts = (date, requestedTimeZone) => {
    const timeZones = [requestedTimeZone, fallbackTimeZone].filter(
      (value, index, values) => value && values.indexOf(value) === index
    );

    for (const timeZone of timeZones) {
      try {
        const formatter = getFormatter('zoned:' + timeZone, 'en-CA', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23'
        });
        const parts = formatter.formatToParts(date);

        return parts.reduce((result, part) => {
          if (part.type !== 'literal') result[part.type] = Number(part.value);
          return result;
        }, {});
      } catch (error) {
        // Older Safari versions may only recognize the legacy Europe/Kiev alias.
      }
    }

    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds()
    };
  };

  const parseCutoff = (value) => {
    const match = String(value || '').match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
    if (!match) return 14 * 60;
    return Number(match[1]) * 60 + Number(match[2]);
  };

  const parseBlackoutDates = (value) => new Set(
    String(value || '')
      .split(/[\s,;]+/)
      .map((date) => date.trim())
      .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
  );

  const settingsFor = (element) => ({
    dispatchMonday: element.dataset.dispatchMonday === 'true',
    dispatchSaturday: element.dataset.dispatchSaturday === 'true',
    dispatchSunday: element.dataset.dispatchSunday === 'true',
    blackoutDates: parseBlackoutDates(element.dataset.blackoutDates)
  });

  const isDispatchDay = (date, settings) => {
    if (settings.blackoutDates.has(dateKey(date))) return false;

    const weekday = date.getUTCDay();
    if (weekday === 0) return settings.dispatchSunday;
    if (weekday === 1) return settings.dispatchMonday;
    if (weekday === 6) return settings.dispatchSaturday;
    return true;
  };

  const nextDispatchDay = (date, settings) => {
    let candidate = date;

    for (let attempts = 0; attempts < 370; attempts += 1) {
      candidate = addCalendarDays(candidate, 1);
      if (isDispatchDay(candidate, settings)) return candidate;
    }

    return date;
  };

  const addDispatchDays = (date, amount, settings) => {
    let candidate = date;
    let remaining = Math.max(0, amount);

    while (remaining > 0) {
      candidate = nextDispatchDay(candidate, settings);
      remaining -= 1;
    }

    return candidate;
  };

  const padDatePart = (value) => String(value).padStart(2, '0');

  const formatDayMonth = (date) => (
    padDatePart(date.getUTCDate()) + '.' + padDatePart(date.getUTCMonth() + 1)
  );

  const formatFullDate = (date) => (
    formatDayMonth(date) + '.' + date.getUTCFullYear()
  );

  const formatSingleDate = (date, today) => {
    const difference = Math.round((date.getTime() - today.getTime()) / dayMs);

    if (difference === 0) return 'сьогодні';
    if (difference === 1) return 'завтра';
    return formatDayMonth(date);
  };

  const formatRange = (from, to, today) => {
    if (dateKey(from) === dateKey(to)) {
      return formatSingleDate(from, today);
    }

    const sameYear = from.getUTCFullYear() === to.getUTCFullYear();
    const sameMonth = sameYear && from.getUTCMonth() === to.getUTCMonth();

    if (sameMonth) {
      return padDatePart(from.getUTCDate()) + '–' + formatDayMonth(to);
    }

    if (sameYear) {
      return formatDayMonth(from) + '–' + formatDayMonth(to);
    }

    return formatFullDate(from) + '–' + formatFullDate(to);
  };

  const render = (element, now) => {
    const valueElement = element.querySelector(valueSelector);
    if (!valueElement) return;

    const parts = zonedParts(now, element.dataset.timeZone || 'Europe/Kyiv');
    const today = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
    const minDays = Math.max(0, toInteger(element.dataset.minDays, 0));
    const maxDays = Math.max(minDays, toInteger(element.dataset.maxDays, 0));
    const settings = settingsFor(element);
    const cutoff = today.getUTCDay() === 1 ? mondayCutoffMinutes : parseCutoff(element.dataset.cutoffTime);
    const currentMinutes = parts.hour * 60 + parts.minute;

    let calculationStart = today;
    if (!isDispatchDay(calculationStart, settings) || currentMinutes >= cutoff) {
      calculationStart = nextDispatchDay(calculationStart, settings);
    }

    const from = addDispatchDays(calculationStart, minDays, settings);
    const to = addDispatchDays(calculationStart, maxDays, settings);
    valueElement.textContent = formatRange(from, to, today);
    valueElement.setAttribute('aria-busy', 'false');
  };

  const millisecondsUntilNextState = (elements, now) => {
    let shortestDelay = dayMs;

    elements.forEach((element) => {
      const parts = zonedParts(now, element.dataset.timeZone || 'Europe/Kyiv');
      const today = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12));
      const settings = settingsFor(element);
      const cutoff = today.getUTCDay() === 1 ? mondayCutoffMinutes : parseCutoff(element.dataset.cutoffTime);
      const currentSeconds = parts.hour * 3600 + parts.minute * 60 + parts.second;
      const midnightSeconds = 24 * 3600;
      let nextBoundarySeconds = midnightSeconds;

      if (isDispatchDay(today, settings) && currentSeconds < cutoff * 60) {
        nextBoundarySeconds = Math.min(nextBoundarySeconds, cutoff * 60);
      }

      const delay = Math.max(1000, (nextBoundarySeconds - currentSeconds) * 1000 + 1000);
      shortestDelay = Math.min(shortestDelay, delay);
    });

    return shortestDelay;
  };

  const renderAll = () => {
    const elements = Array.from(document.querySelectorAll(selector));
    if (!elements.length) {
      window.clearTimeout(refreshTimer);
      refreshTimer = null;
      return;
    }

    const now = new Date();
    elements.forEach((element) => render(element, now));

    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(renderAll, millisecondsUntilNextState(elements, now));
  };

  window[namespace] = { renderAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderAll, { once: true });
  } else {
    renderAll();
  }

  document.addEventListener('shopify:section:load', renderAll);
  document.addEventListener('shopify:section:unload', () => window.setTimeout(renderAll, 0));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) renderAll();
  });
  window.addEventListener('pageshow', renderAll);
})();